import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { LOGARITHMIC_META, type LogarithmicParams } from './logarithmic.model';

/** Logarithmic params with index signature for JsxParamStore compatibility */
type LogarithmicParamsIndexed = LogarithmicParams & { [key: string]: number };

/**
 * Creates logarithmic functions: a * log_b(x-h) + k
 *
 * Parameters:
 * - a: vertical scale
 * - b: base of logarithm
 * - h: horizontal shift (vertical asymptote at x = h)
 * - k: vertical shift
 */
export class LogarithmicCreator extends BaseFunctionCreator<LogarithmicParamsIndexed> {
  readonly functionType = 'logarithmic';
  readonly defaultParams: LogarithmicParamsIndexed = { ...LOGARITHMIC_META.defaultParams };
  readonly availableFeatures = LOGARITHMIC_META.availableFeatures;
  readonly handleConfigs = LOGARITHMIC_META.handleConfigs;
  override readonly computedValueKeys = ['verticalAsymptote', 'yIntercept'] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as LogarithmicParamsIndexed;

    const colorName = (display?.curve?.color ?? 'teal') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: LOGARITHMIC_META.defaultRanges.a },
      b: { value: initialParams.b, range: LOGARITHMIC_META.defaultRanges.b },
      h: { value: initialParams.h, range: LOGARITHMIC_META.defaultRanges.h },
      k: { value: initialParams.k, range: LOGARITHMIC_META.defaultRanges.k },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      const arg = x - h;
      if (arg <= 0) {
        return NaN;
      }
      // log_b(x) = ln(x) / ln(b)
      return a * (Math.log(arg) / Math.log(b)) + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // Derivative of a * log_b(x-h) + k is a / ((x-h) * ln(b))
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const h = paramStore.getValue('h');
          const arg = x - h;
          if (arg <= 0) {
            return NaN;
          }
          return a / (arg * Math.log(b));
        };
      }
      if (order === 2) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const h = paramStore.getValue('h');
          const arg = x - h;
          if (arg <= 0) {
            return NaN;
          }
          return -a / (arg * arg * Math.log(b));
        };
      }
      // General formula: (-1)^(n-1) * (n-1)! * a / ((x-h)^n * ln(b))
      return (x: number) => {
        const a = paramStore.getValue('a');
        const b = paramStore.getValue('b');
        const h = paramStore.getValue('h');
        const arg = x - h;
        if (arg <= 0) {
          return NaN;
        }
        const sign = order % 2 === 1 ? 1 : -1;
        let factorial = 1;
        for (let i = 2; i < order; i++) {
          factorial *= i;
        }
        return (sign * factorial * a) / (Math.pow(arg, order) * Math.log(b));
      };
    };

    return {
      id,
      type: 'logarithmic',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as LogarithmicParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      getComputedValues: () => ({
        verticalAsymptote: () => paramStore.getValue('h'),
        yIntercept: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const h = paramStore.getValue('h');
          const k = paramStore.getValue('k');
          if (h >= 0) {
            return NaN;
          }
          return a * (Math.log(-h) / Math.log(b)) + k;
        },
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
